import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { FileText, Download, Loader2, Paperclip, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const fmtSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Shared booking attachments card — used by clients (role="client") and providers (role="provider").
export default function BookingDocuments({ bookingId, role = 'client' }) {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const base = role === 'provider'
    ? `${API_URL}/api/dashboard/bookings/${bookingId}/documents`
    : `${API_URL}/api/client/bookings/${bookingId}/documents`;
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(base, { headers });
      setDocs(res.data || []);
    } catch { /* optional */ }
  };

  useEffect(() => { if (bookingId) load(); /* eslint-disable-next-line */ }, [bookingId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(base, fd, { headers });
      toast.success('Document attached');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await axios.get(`${base}/${d.id}/file`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download document'); }
  };

  const remove = async (d) => {
    try {
      await axios.delete(`${base}/${d.id}`, { headers });
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
      toast.success('Document removed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not remove document'); }
  };

  const mine = (d) => d.uploaderId === user?.id || (role === 'provider' && d.uploadedBy === 'provider') || (role === 'client' && d.uploadedBy === 'client');

  return (
    <Card data-testid="booking-documents">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Paperclip className="h-4 w-4" /> Documents</CardTitle>
        <>
          <input ref={fileRef} type="file" className="hidden" onChange={upload}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt"
            data-testid="document-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="add-document-btn">
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Paperclip className="h-3 w-3 mr-1" />} Attach File
          </Button>
        </>
      </CardHeader>
      <CardContent>
        {docs.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="documents-empty">No documents yet. Attach vet records, waivers, or any paperwork for this booking.</p>
        ) : (
          <ul className="space-y-2">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-2 rounded-md border p-2 text-sm" data-testid={`booking-document-${d.id}`}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{d.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {fmtSize(d.size)} · by {d.uploadedBy === 'provider' ? 'provider' : 'client'}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`download-document-${d.id}`} aria-label="Download">
                  <Download className="h-4 w-4" />
                </Button>
                {mine(d) && (
                  <Button size="sm" variant="ghost" onClick={() => remove(d)} data-testid={`delete-document-${d.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
